import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useMe } from 'contexts/me'
import { size, omit } from 'utilities/object'
import { compact } from 'utilities/array'
import { useCheckDocumentPermission, useCombineAccessUnits, splitAccessUnits } from 'pages/documents/utilities'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import SelectField from 'components/form/field/select'
import PersonField from 'components/form/field/person'
import AccessUnitsField from 'components/form/field/access-units'
import QuickSelectAccessUnits from 'pages/documents/components/quick-select-access-units'
import CheckboxField from 'components/form/field/checkbox'
import AccessWarnings from 'pages/documents/components/access-warnings'
import DocumentReference from './document-reference'
import Caption from 'components/typography/caption'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const PreviewDocumentDetailsFields = ({
    dokument, setDokument,
    editing, setEditing,
    categories,
    previewAnother,
    salt
}) => {
    const { formatMessage } = useIntl()

    const {
        fetchDocument,
        updateDocument
    } = useDocuments()

    const { getListMe } = useMe()

    const checkDocumentPermission = useCheckDocumentPermission()
    const combineAccessUnits = useCombineAccessUnits()

    const [concerns, setConcerns] = useState(dokument.concerns ?? null)
    const [whistle, setWhistle] = useState(Date.now())
    const [access, setAccess] = useState({
        value: combineAccessUnits(dokument),
        changed: false
    })

    const [updating, setUpdating] = useState(false)
    const [documentReference, setDocumentReference] = useState(null)

    let {
        id,
        name,
        extension,
        category,
        documentRefId
    } = dokument

    category = category ?? 'none'

    useEffect(() => {
        if(!!documentRefId) {
            const fetchDocumentReference = async () => {
                const { ok, response } = await fetchDocument(documentRefId)

                if(ok && response) {
                    setDocumentReference(response)
                }
            }

            fetchDocumentReference()
        }
    }, [documentRefId])

    const update = async (body, { resetTouched }) => {
        setUpdating(true)

        const {
            ok,
            response: dokument,
            // meta // TODO: If lost access completely, close modal
        } = await updateDocument({
            ...omit(body, 'access'),
            ...(access.changed ? splitAccessUnits(access.value) : null),
            ...((body.category === 'none') ? { category: null } : null)
        }, id)

        setUpdating(false)

        if(ok) {
            setDokument(dokument)
            resetTouched()
            setEditing(null)
        }
    }

    const { editable } = checkDocumentPermission(dokument)
    const noOwners = !access.value.some(({ access }) => access.owner)

    return (
        <Form
            layout="vertical"
            onSubmit={update}
            key={`${salt}:preview`}>
            {({ touched, values, errors, trigger }) => (
                <>
                    {!!editing && (
                        <>
                            <StringField
                                salt={salt}
                                label={formatMessage({
                                    id: 'noun_name',
                                    defaultMessage: 'Name'
                                })}
                                name="name"
                                field={{
                                    value: name,
                                    suffix: extension ? `.${extension}` : null,
                                    required: true
                                }}
                                controlProps={{ autoFocus: editing === 'auto' }}
                                view={{ ellipsify: { tail: 9 } }} />
                            <SelectField
                                salt={salt}
                                label={formatMessage({
                                    id: 'noun_category',
                                    defaultMessage: 'Category'
                                })}
                                name="category"
                                field={{
                                    value: category,
                                    include: 'touched',
                                    allowEmptyOption: false
                                }}
                                options={categories.map(category => ({
                                    ...category,
                                    key: category.value
                                }))} />
                        </>
                    )}
                    {(!!concerns || !!editing) && (
                        <PersonField
                            salt={salt}
                            label={formatMessage({
                                id: 'document_label_regarding',
                                defaultMessage: 'Regarding'
                            })}
                            name="concerns"
                            field={{
                                value: concerns,
                                editable: editing,
                                include: 'touched',
                                optional: formatMessage({
                                    id: 'document_label_regarding_optional',
                                    defaultMessage: 'Optional: Specify who the document regards'
                                })
                            }}
                            picker={{ outer: false }}
                            onChange={({ concerns }) => setConcerns(concerns)} />
                    )}
                    <AccessUnitsField
                        salt={salt}
                        label={formatMessage({
                            id: 'noun_access',
                            defaultMessage: 'Access'
                        })}
                        name="access"
                        field={{
                            value: access.value,
                            editable: editing,
                            include: access.changed ?
                                'always' :
                                'touched'
                        }}
                        controlProps={{ autoFocus: editing === 'access' }}
                        {...(!!editable ? {
                            display: {
                                view: (
                                    <Plain
                                        onClick={() => setEditing('access')}
                                        className="constructive">
                                        <FormattedMessage
                                            id="access_action_manage"
                                            defaultMessage="Manage access" />
                                    </Plain>
                                )
                            }
                        } : null)}
                        formatAdminTooltip={unit => formatMessage({
                            id: 'document_unit_administrator_description',
                            defaultMessage: 'Users with roles that grant document access in <strong>{name}</strong>'
                        }, unit)}
                        belowSearchContent={(
                            <QuickSelectAccessUnits
                                options={compact([
                                    ...(concerns ? [{
                                        unit: concerns,
                                        access: {
                                            share: true,
                                            owner: false
                                        }
                                    }] : []),
                                    {
                                        unit: getListMe(),
                                        access: {
                                            share: true,
                                            owner: true
                                        }
                                    }
                                ])}
                                values={access.value}
                                insertValue={value => {
                                    setAccess({
                                        value: [
                                            value,
                                            ...access.value
                                        ],
                                        changed: true
                                    })

                                    setWhistle(Date.now())
                                }} />
                        )}
                        onChange={({ access }) => setAccess({
                            value: access,
                            changed: true
                        })}
                        whistle={whistle} />
                    {!!editing && (
                        <AccessWarnings
                            access={access}
                            setAccess={access => {
                                setAccess(access)
                                setWhistle(Date.now())
                            }} />
                    )}
                    {documentReference && (
                        <DocumentReference
                            dokument={documentReference}
                            preview={previewAnother}
                            salt={salt} />
                    )}
                    {(!!editable && !!editing) && (
                        <>
                            <CheckboxField
                                salt={salt}
                                {...(!values?.notify ? { className: 'compact' } : null)}
                                label={formatMessage({
                                    id: 'notification_toggle_document_update_label',
                                    defaultMessage: 'Send notification when updating'
                                })}
                                name="notify"
                                field={{
                                    value: false,
                                    include: 'always'
                                }}
                                interaction="switch" />
                            {!!values?.notify && (
                                <Caption>
                                    <FormattedMessage
                                        id="notification_toggle_document_upload_description"
                                        defaultMessage="A notification will be sent to those the document will be shared with and who has opted to receive notifications." />
                                </Caption>
                            )}
                            <Actions>
                                <Plain
                                    onClick={() => {
                                        setEditing(null)

                                        setAccess({
                                            value: combineAccessUnits(dokument),
                                            changed: false
                                        })
                                    }}
                                    className="neutral"
                                    disabled={updating}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={`constructive${updating ? ' loading' : ''}`}
                                    disabled={(!touched.length && !access.changed) || !!size(errors) || noOwners || updating}
                                    ref={trigger}>
                                    <FormattedMessage
                                        id="action_save"
                                        defaultMessage="Save" />
                                </ButtonSubmit>
                            </Actions>
                        </>
                    )}
                </>
            )}
        </Form>
    )
}

export default PreviewDocumentDetailsFields